import router from '@/router'
import Cookies from 'js-cookie'
import { Message } from 'element-ui'
import conditionsApi from '@/api/conditions'
import $lodash from 'lodash'

const types = {
  SET_USER_INFO: 'root/SET_USER_INFO',
  SET_DATA_LOADING: 'root/SET_DATA_LOADING',
  SET_RECENT_INPUTS: 'root/SET_RECENT_INPUTS',
  SET_BASEINFO: 'root/SET_BASEINFO',
  RESET_BASEINFO: 'root/RESET_BASEINFO',
  SET_DEFAULT_CLIENT_AND_AGENCY_NAME: 'root/SET_DEFAULT_CLIENT_AND_AGENCY_NAME',
  SET_BASEINFO_CHECKED: 'root/SET_BASEINFO_CHECKED',
  UPDATE_TERMS_AUTHORIZATION_TRUE: 'root/UPDATE_TERMS_AUTHORIZATION_TRUE',
  SET_SHOW_UPDATE_PASSWORD: 'root/SET_SHOW_UPDATE_PASSWORD'
}

const defaultBaseInfo = () => {
  return {
    campaignName: null,
    clientId: null,
    clientName: null,
    agencyId: null,
    agencyName: null
  }
}

const state = {
  userInfo: null,
  dataLoading: false,
  recentInputs: [],
  baseInfo: defaultBaseInfo(),
  baseInfoChecked: false,
  currentTab: 'speed', // 'speed' or 'direct'
  showUpdatePassword: false
}

const getters = {
  userId: state => state.userInfo && state.userInfo.id,
  userName: state => {
    if (state.userInfo && state.userInfo.email) {
      return state.userInfo.email.split('@')[0]
    } else {
      return null
    }
  },
  userRole: state => state.userInfo && Object.values(state.userInfo.roles),
  userIsReseller: state => state.userInfo && Object.values(state.userInfo.roles).includes('qp_reseller'),
  userIsAgency: state => state.userInfo && Object.values(state.userInfo.roles).includes('qp_agency'),
  userIsClient: state => state.userInfo && Object.values(state.userInfo.roles).includes('qp_client'),
  userType: state => state.userInfo && state.userInfo.type, // 'User', 'Account'
  userPartyType: state => state.userInfo && state.userInfo.party_type, // 'Client', 'Agency', 'Media',
  userParties: state => state.userInfo && state.userInfo.parties,
  userClientName: state => {
    if (state.userInfo === null) return null
    if (state.userInfo.client_name === undefined) return null
    if (state.userInfo.client_name.length === 0) return null
    return state.userInfo.client_name[0]
  },
  userAgencyName: state => {
    if (state.userInfo === null) return null
    if (state.userInfo.agency_name === undefined) return null
    if (state.userInfo.agency_name.length === 0) return null
    return state.userInfo.agency_name[0]
  },
  recentCampaigns: state => {
    if (state.recentInputs.length === 0) return []
    let campaignNames = state.recentInputs.map(input => input.name)
    campaignNames = [...new Set(campaignNames)] // uniq
    let result = []
    campaignNames.forEach(name => result.push({ 'value': name }))
    return result
  },
  recentClientNames: state => {
    if (state.recentInputs.length === 0) return []
    let clientNames = state.recentInputs.map(input => input.client_name)
    clientNames = [...new Set(clientNames)] // uniq
    let result = []
    clientNames.forEach(name => result.push({ 'value': name }))
    return result
  },
  recentAgencyNames: state => {
    if (state.recentInputs.length === 0) return []
    let agencyNames = state.recentInputs.map(input => input.agency_name)
    agencyNames = [...new Set(agencyNames)] // uniq
    let result = []
    agencyNames.forEach(name => result.push({ 'value': name }))
    return result
  }
}

const actions = {
  // 當API發送請求時會被使用， 參照：'src/utils/request'
  setDataLoading ({ commit }, data) {
    commit(types.SET_DATA_LOADING, data)
  },
  // 登入後呼叫
  setUserInfo ({ commit, dispatch, getters }, data) {
    commit(types.SET_USER_INFO, data)
    let payload = {}
    if (getters['userClientName'] && (getters['userIsClient'] || getters['userIsAgency'] || getters['userIsReseller'])) payload.userClientName = getters['userClientName']
    if (getters['userAgencyName'] && (getters['userIsAgency'] || getters['userIsReseller'])) payload.userAgencyName = getters['userAgencyName']
    // 設定代理商名或是客戶名
    dispatch('setDefaultClientAndAgencyName', payload)
  },
  // 清空選擇資料 但如果是 agency 或 client，要再設定代理商名或是客戶名
  resetSelectedData ({ dispatch, getters }) {
    let payload = {}
    if (getters['userClientName'] && (getters['userIsClient'] || getters['userIsAgency'])) payload.userClientName = getters['userClientName']
    if (getters['userAgencyName'] && getters['userIsAgency']) payload.userAgencyName = getters['userAgencyName']
    dispatch('resetBaseInfo', payload)
    // 設定代理商名或是客戶名
    dispatch('setDefaultClientAndAgencyName', payload)
  },
  // 登出邏輯 (登入邏輯於 src/views/Login/index.vue 中)
  systemLogout ({ commit, dispatch }, message) {
    // 登出時先將Cookie, Vuex, sessionStorage的用戶相關資訊移除
    commit(types.SET_USER_INFO, null)
    dispatch('resetSelectedData')
    dispatch('speedCalculation2/resetSelectedData', null, { root: true })
    Cookies.remove('qp_access_token')
    window.localStorage.setItem('systemLogout', new Date())
    window.sessionStorage.removeItem('qpUserInfo')
    router.push({ name: 'Login' })
    Message.warning(message)
  },
  // 取得 user 最近輸入的廣告名、客戶名
  setRecentInputs ({ commit }, payload) {
    conditionsApi.recentInputs().then((res) => {
      if (res.data.data === undefined) return
      let recentInputs = res.data.data.sort((a, b) => b.id - a.id)
      commit(types.SET_RECENT_INPUTS, recentInputs)
    })
  },
  setBaseInfo ({ commit }, payload) {
    commit(types.SET_BASEINFO, payload)
  },
  resetBaseInfo ({ commit }, payload) {
    commit(types.RESET_BASEINFO, payload)
  },
  // 設定代理商名或是客戶名
  setDefaultClientAndAgencyName ({ commit }, data) {
    commit(types.SET_DEFAULT_CLIENT_AND_AGENCY_NAME, data)
  },
  updateBaseInfoChecked ({ commit }, data) {
    commit(types.SET_BASEINFO_CHECKED, data)
  },
  updateTermsAuthorizationTrue ({ commit }) {
    commit(types.UPDATE_TERMS_AUTHORIZATION_TRUE)
  },
  setShowUpdatePassword ({ commit }, data) {
    commit(types.SET_SHOW_UPDATE_PASSWORD, data)
  }
}

const mutations = {
  [types.SET_USER_INFO] (state, data) {
    state.userInfo = data
  },
  [types.SET_DATA_LOADING] (state, data) {
    state.dataLoading = data
  },
  [types.SET_RECENT_INPUTS] (state, data) {
    state.recentInputs = data
  },
  [types.SET_BASEINFO] (state, data) {
    state.baseInfo = $lodash.cloneDeep(data)
  },
  [types.SET_DEFAULT_CLIENT_AND_AGENCY_NAME] (state, data) {
    if (data.userClientName) state.baseInfo.clientName = data.userClientName
    if (data.userAgencyName) state.baseInfo.agencyName = data.userAgencyName
  },
  [types.RESET_BASEINFO] (state) {
    state.baseInfo = defaultBaseInfo()
  },
  [types.SET_BASEINFO_CHECKED] (state, data) {
    state.baseInfoChecked = data
  },
  [types.UPDATE_TERMS_AUTHORIZATION_TRUE] (state) {
    state.userInfo.qp_authorization = true
  },
  [types.SET_SHOW_UPDATE_PASSWORD] (state, data) {
    state.showUpdatePassword = data
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
